import React, { useEffect, useState } from "react";
import { Avatar, Transfer, Typography, notification } from "antd";
import type { TransferItem } from "antd/es/transfer";
import { useParams } from "react-router-dom";
import { IBettingWebsiteLanguage } from "../../../types/BettingWebsite.Language";
import { getLanguages } from "../../Languages/action";
import {
  getBettingWebsiteByID,
  pullFromBettingWebsiteLanguages,
  pushIntoBettingWebsiteLanguages,
} from "../action";

const { Title } = Typography;

const EditLanguages: React.FC<{ fetchData: () => void }> = ({ fetchData }) => {
  const { id } = useParams();
  const [languages, setLanguages] = useState<IBettingWebsiteLanguage[]>([]);
  const [target, setTarget] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const fetchLanguages = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const [allLangs, website] = await Promise.all([
        getLanguages(),
        getBettingWebsiteByID(id),
      ]);

      setLanguages(allLangs);
      const selectedIds =
        website?.Languages?.map((lang: IBettingWebsiteLanguage) =>
          String(lang.id)
        ) || [];
      setTarget(selectedIds);
    } catch (err: any) {
      api.error({
        message: "Failed to load data",
        description: err?.message || "Unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLanguages();
  }, [id]);

  const handleChange = async (
    nextTargetKeys: (string | number)[],
    _direction: "left" | "right",
    _moveKeys: (string | number)[]
  ) => {
    const stringNextTargetKeys = nextTargetKeys.map(String);

    const toPush = stringNextTargetKeys.filter((id) => !target.includes(id));
    const toPull = target.filter((id) => !stringNextTargetKeys.includes(id));

    setTarget(stringNextTargetKeys);
    setIsLoading(true);

    try {
      for (const langId of toPush) {
        await pushIntoBettingWebsiteLanguages(id!, langId);
      }

      for (const langId of toPull) {
        await pullFromBettingWebsiteLanguages(id!, langId);
      }

      api.success({ message: "Languages updated successfully" });
      fetchData();
    } catch (err: any) {
      api.error({
        message: "Failed to update languages",
        description:
          err?.response?.errors?.[0]?.message ||
          err?.message ||
          "Unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const transferData: TransferItem[] = languages.map((lang) => ({
    key: String(lang.id),
    title: lang.Country.En ?? "",
    flag: lang.Flag,
  }));

  return (
    <>
      {contextHolder}
      <Transfer
        dataSource={transferData}
        targetKeys={target}
        onChange={handleChange}
        titles={["Available Languages", "Selected Languages"]}
        render={(item) => {
          const flag = (item as any).flag || "";
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Avatar
                src={`https://flagcdn.com/${flag.toLowerCase()}.svg`}
                size="small"
              />
              <span>{item.title}</span>
            </div>
          );
        }}
        filterOption={(inputValue, item) =>
          (item.title ?? "").toLowerCase().includes(inputValue.toLowerCase())
        }
        oneWay
        disabled={isLoading}
        pagination
        showSearch
        style={{ width: "100%" }} // make the Transfer full width
        listStyle={{
          width: "48%", // each list takes 48% width
          height: 400,
        }}
      />
    </>
  );
};

export default EditLanguages;
